// frontend/src/pages/Admin/AdminDocumentManagementPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { listDocuments, uploadDocument, getDownloadUrl, deleteDocument } from '../../services/documentService';
import { getAllUsers } from '../../services/userService';
import { listAllAccounts } from '../../services/accountService';
import styles from './AdminDocumentManagementPage.module.css';
import Button from '../../components/Button/Button';

const AdminDocumentManagementPage = () => {
    const [documents, setDocuments] = useState([]);
    const [users, setUsers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // State for the upload form
    const [selectedFile, setSelectedFile] = useState(null);
    const [description, setDescription] = useState('');
    const [isGlobal, setIsGlobal] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState('');

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // If an account is selected, we fetch docs for that account.
            // Otherwise, we fetch EVERYTHING (all=true) for the initial view or global view.
            const docsParams = selectedAccountId ? { accountId: selectedAccountId } : { all: true };

            const [docsData, usersData, accountsData] = await Promise.all([
                listDocuments(docsParams),
                getAllUsers(),
                listAllAccounts()
            ]);
            setDocuments(docsData || []);
            setUsers(usersData || []);
            setAccounts(accountsData || []);
        } catch (err) {
            setError(err.message || 'Failed to load data.');
        } finally {
            setIsLoading(false);
        }
    }, [selectedAccountId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setUploadSuccess('');
        setUploadError(null);
    };

    const handleUserSelection = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSubmitUpload = async (event) => {
        event.preventDefault();
        if (!selectedFile) {
            setUploadError('Please select a file to upload.');
            return;
        }
        if (!description.trim()) {
            setUploadError('Please enter a description.');
            return;
        }

        const formData = new FormData();
        formData.append('documentFile', selectedFile);
        formData.append('description', description);
        formData.append('isGlobal', isGlobal);

        // If not global, associate with selected account or target users
        if (!isGlobal) {
            if (selectedUserIds.length > 0) {
                formData.append('targetUserIds', JSON.stringify(selectedUserIds));
            } else if (selectedAccountId) {
                formData.append('accountId', selectedAccountId);
            }
        }

        setUploading(true);
        setUploadError(null);
        setUploadSuccess('');

        try {
            await uploadDocument(formData);
            setUploadSuccess('Document uploaded successfully!');
            setSelectedFile(null);
            setDescription('');
            setIsGlobal(false);
            setSelectedUserIds([]);
            const fileInput = document.getElementById('adminDocumentFileInput');
            if (fileInput) fileInput.value = null;
            fetchData();
            setTimeout(() => setUploadSuccess(''), 5000);
        } catch (err) {
            setUploadError(err.message || 'Upload failed.');
        } finally {
            setUploading(false);
        }
    };

    const handleView = async (docId) => {
        try {
            const { downloadUrl } = await getDownloadUrl(docId);
            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            alert(`Failed to open document: ${err.message}`);
        }
    };

    const handleDelete = async (docId, fileName) => {
        if (!window.confirm(`Delete document "${fileName}"?`)) return;
        try {
            await deleteDocument(docId);
            fetchData();
        } catch (err) {
            alert(`Delete failed: ${err.message}`);
        }
    };

    const globalDocs = documents.filter(doc => doc.isGlobal);
    const accountDocs = documents.filter(doc => !doc.isGlobal);

    const renderDocsTable = (docsList, emptyMsg = "No documents found.") => {
        if (docsList.length === 0) return <p className={styles.emptyMsg}>{emptyMsg}</p>;

        return (
            <table className={styles.docsTable}>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Target</th>
                        <th>Uploaded By</th>
                        <th>Date</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {docsList.map(doc => (
                        <tr key={doc.id}>
                            <td>{doc.fileName}</td>
                            <td>
                                {doc.isGlobal ? 'Global' : (
                                    doc.targetedUsers?.length > 0
                                        ? `Users: ${doc.targetedUsers.map(u => u.username).join(', ')}`
                                        : (doc.accountId ? `Account ID: ${doc.accountId}` : 'Private')
                                )}
                            </td>
                            <td>{doc.uploader?.username || 'System'}</td>
                            <td>{new Date(doc.createdAt).toLocaleDateString()}</td>
                            <td>
                                <div className={styles.actionBtns}>
                                    <Button onClick={() => handleView(doc.id)} size="sm" variant="secondary">View</Button>
                                    <Button onClick={() => handleDelete(doc.id, doc.fileName)} size="sm" variant="secondary">Delete</Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <div className={styles.adminDocsContainer}>
            <h2 className="page-main-title">Admin Document Management</h2>

            <div className={styles.topSection}>
                <div className={styles.uploadCard}>
                    <h3>Upload Document</h3>
                    <form onSubmit={handleSubmitUpload}>
                        <div className={styles.formGroup}>
                            <label>File (PDF):</label>
                            <input
                                type="file"
                                id="adminDocumentFileInput"
                                accept=".pdf"
                                onChange={handleFileChange}
                                disabled={uploading}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Description:</label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Brief description"
                                disabled={uploading}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={isGlobal}
                                    onChange={(e) => setIsGlobal(e.target.checked)}
                                    disabled={uploading}
                                />
                                Available to all users (Global)
                            </label>
                        </div>

                        {!isGlobal && (
                            <>
                                <div className={styles.formGroup}>
                                    <label>Target Account (optional if picking users):</label>
                                    <select
                                        value={selectedAccountId}
                                        onChange={(e) => setSelectedAccountId(e.target.value)}
                                        disabled={uploading}
                                        className={styles.accountSelect}
                                    >
                                        <option value="">-- All Accounts / System Defaults --</option>
                                        {accounts.map(acc => (
                                            <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.userSelectionArea}>
                                    <label>Target Users (optional):</label>
                                    <div className={styles.usersList}>
                                        {users.map(user => (
                                            <div key={user.id} className={styles.userItem}>
                                                <input
                                                    type="checkbox"
                                                    id={`user-${user.id}`}
                                                    checked={selectedUserIds.includes(user.id)}
                                                    onChange={() => handleUserSelection(user.id)}
                                                    disabled={uploading}
                                                />
                                                <label htmlFor={`user-${user.id}`}>{user.username} ({user.role})</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        <Button type="submit" disabled={uploading} variant="primary">
                            {uploading ? 'Uploading...' : 'Upload Document'}
                        </Button>
                    </form>
                    {uploadError && <p className={styles.errorMessage}>{uploadError}</p>}
                    {uploadSuccess && <p className={styles.successMessage}>{uploadSuccess}</p>}
                </div>

                <div className={styles.filterCard}>
                    <h3>Filter Documents</h3>
                    <div className={styles.formGroup}>
                        <label>Select Account to View:</label>
                        <select
                            value={selectedAccountId}
                            onChange={(e) => setSelectedAccountId(e.target.value)}
                            className={styles.accountSelect}
                        >
                            <option value="">Show All Accounts</option>
                            {accounts.map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className={styles.docsListSection}>
                <div className={styles.sectionHeader}>
                    <h3>1. Global Documents</h3>
                    <p className={styles.sectionSubtitle}>Visible to all users on their Documentation page.</p>
                </div>
                {isLoading ? <p>Loading global documents...</p> : renderDocsTable(globalDocs, "No global documents found.")}

                <div className={styles.sectionHeader} style={{ marginTop: '2.5rem' }}>
                    <h3>2. Account Specific Documents</h3>
                    <p className={styles.sectionSubtitle}>
                        {selectedAccountId
                            ? `Private documents for account: ${accounts.find(a => a.id === parseInt(selectedAccountId))?.accountName || '...'}`
                            : 'All private documents across all accounts.'}
                    </p>
                </div>
                {isLoading ? <p>Loading account documents...</p> : renderDocsTable(accountDocs, selectedAccountId ? "No private documents found for this account." : "No private documents found.")}
            </div>
        </div>
    );
};

export default AdminDocumentManagementPage;
